/**
 * Generic CSV generation and print utilities for data export.
 */

const UTF8_BOM = "\uFEFF";

/**
 * Escape a CSV field value per RFC 4180.
 * Wraps in double quotes if the value contains commas, double quotes,
 * or newlines. Existing double quotes are doubled.
 */
function escapeCSVField(value: string): string {
  let sanitized = value;

  // Prevent CSV injection by prefixing formula-trigger characters (OWASP),
  // including when they are preceded by optional leading whitespace.
  const FORMULA_TRIGGER_PATTERN = /^[\s]*[=+\-@\t\r]/;
  if (FORMULA_TRIGGER_PATTERN.test(sanitized)) {
    sanitized = "'" + sanitized;
  }

  // RFC 4180: wrap in double quotes if field contains delimiters
  if (
    sanitized.includes(",") ||
    sanitized.includes('"') ||
    sanitized.includes("\n") ||
    sanitized.includes("\r")
  ) {
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
}

/**
 * Generate a CSV file and trigger a browser download.
 *
 * @param headers  - Column header labels
 * @param rows     - 2D array of string cell values
 * @param filename - Download filename (should end in .csv)
 */
export function generateCSV(
  headers: string[],
  rows: string[][],
  filename: string,
): void {
  const headerLine = headers.map(escapeCSVField).join(",");
  const dataLines = rows.map((row) => row.map(escapeCSVField).join(","));
  const csvContent = UTF8_BOM + [headerLine, ...dataLines].join("\r\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Trigger the browser's native print dialog for PDF export.
 */
export function triggerPrint(): void {
  window.print();
}
